<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBoletoClients } from 'dashboard/composables/useBoletoClients';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

import ClientCard from './components/ClientCard.vue';
import AddClientModal from './components/AddClientModal.vue';
import ConfigModal from './components/ConfigModal.vue';
import UploadModal from './components/UploadModal.vue';
import ConfirmModal from './components/ConfirmModal.vue';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const {
  clients,
  stats,
  loading,
  uploading,
  fetchClients,
  fetchStats,
  createClient,
  updateClient,
  deleteClient,
  sendNow,
  uploadFiles,
} = useBoletoClients();

// UI State
const searchQuery = ref('');
const filterStatus = ref('all');
const showAddModal = ref(false);
const showConfigModal = ref(false);
const showUploadModal = ref(false);
const selectedClient = ref(null);

// Confirm modal state
const confirmModal = ref({
  isOpen: false,
  title: '',
  message: '',
  type: 'warning',
  loading: false,
  action: null,
  confirmText: '',
});

// Store refs for client cards
const clientCardRefs = ref({});

onMounted(async () => {
  await Promise.all([fetchClients(), fetchStats()]);
});

const filteredClients = computed(() => {
  let result = clients.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      c =>
        c.contact.name.toLowerCase().includes(query) ||
        c.contact.phone_number?.includes(query)
    );
  }

  if (filterStatus.value !== 'all') {
    result = result.filter(c =>
      filterStatus.value === 'active' ? c.active : !c.active
    );
  }

  return result;
});

const openConfig = client => {
  selectedClient.value = client;
  showConfigModal.value = true;
};

const openUpload = client => {
  selectedClient.value = client;
  showUploadModal.value = true;
};

const handleCreate = async clientData => {
  await createClient(clientData);
  showAddModal.value = false;
};

const handleUpdate = async clientData => {
  if (selectedClient.value) {
    await updateClient(selectedClient.value.id, clientData);
    showConfigModal.value = false;
  }
};

// Confirm delete with modal
const handleDelete = clientId => {
  const client = clients.value.find(c => c.id === clientId);
  confirmModal.value = {
    isOpen: true,
    title: t('OMNI.BOLETOS.CONFIRM_DELETE_TITLE'),
    message: t('OMNI.BOLETOS.CONFIRM_DELETE_MESSAGE', { name: client?.contact?.name || '' }),
    type: 'danger',
    loading: false,
    confirmText: t('OMNI.BOLETOS.DELETE'),
    action: async () => {
      confirmModal.value.loading = true;
      try {
        await deleteClient(clientId);
      } finally {
        closeConfirmModal();
      }
    },
  };
};

// Confirm send with modal
const handleSendNow = (clientId, boletoFileId) => {
  const client = clients.value.find(c => c.id === clientId);
  const boleto = client?.boleto_files?.find(b => b.id === boletoFileId);
  const isResend = (boleto?.times_sent || 0) >= (client?.send_count || 1);
  
  confirmModal.value = {
    isOpen: true,
    title: isResend ? t('OMNI.BOLETOS.CONFIRM_RESEND_TITLE') : t('OMNI.BOLETOS.CONFIRM_SEND_TITLE'),
    message: isResend 
      ? t('OMNI.BOLETOS.CONFIRM_RESEND_MESSAGE', { month: boleto?.month_label || '', count: boleto?.times_sent || 0 })
      : t('OMNI.BOLETOS.CONFIRM_SEND_MESSAGE', { month: boleto?.month_label || '', name: client?.contact?.name || '' }),
    type: isResend ? 'warning' : 'info',
    loading: false,
    confirmText: isResend ? t('OMNI.BOLETOS.RESEND') : t('OMNI.BOLETOS.SEND_NOW'),
    action: async () => {
      confirmModal.value.loading = true;
      try {
        await sendNow(clientId, boletoFileId);
        // Clear selection on the card
        const cardRef = clientCardRefs.value[clientId];
        if (cardRef?.clearSelection) {
          cardRef.clearSelection();
        }
      } finally {
        closeConfirmModal();
      }
    },
  };
};

const closeConfirmModal = () => {
  confirmModal.value = {
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    loading: false,
    action: null,
    confirmText: '',
  };
};

const executeConfirmAction = () => {
  if (confirmModal.value.action) {
    confirmModal.value.action();
  }
};

const handleUpload = async ({ files, monthMappings }) => {
  if (selectedClient.value) {
    await uploadFiles(selectedClient.value.id, files, monthMappings);
    showUploadModal.value = false;
  }
};

// Set ref for client card
const setClientCardRef = (clientId, el) => {
  if (el) {
    clientCardRefs.value[clientId] = el;
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0 bg-n-background">
    <!-- Header -->
    <header
      class="bg-n-solid-2 border-b border-n-weak px-8 py-6"
    >
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">
            {{ t('OMNI.BOLETOS.TITLE') }}
          </h1>
          <p class="text-n-slate-11 mt-1">
            {{ t('OMNI.BOLETOS.DESCRIPTION') }}
          </p>
        </div>
        <button
          v-if="canEditSection('boletos')"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-woot-500 text-white rounded-lg hover:bg-woot-600 font-medium shadow-sm"
          @click="showAddModal = true"
        >
          <span class="i-lucide-user-plus text-lg" />
          {{ t('OMNI.BOLETOS.ADD_CLIENT') }}
        </button>
      </div>

      <!-- Stats Bar -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <!-- Total Clients -->
        <div class="bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-800/50 flex items-center justify-center">
              <span class="i-lucide-users text-xl text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-violet-700 dark:text-violet-300">
                {{ stats.total_clients }}
              </div>
              <div class="text-xs text-violet-600/70 dark:text-violet-400/70">
                {{ t('OMNI.BOLETOS.STATS.CLIENTS') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Active Clients -->
        <div class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-800/50 flex items-center justify-center">
              <span class="i-lucide-user-check text-xl text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-green-700 dark:text-green-300">
                {{ stats.active_clients }}
              </div>
              <div class="text-xs text-green-600/70 dark:text-green-400/70">
                {{ t('OMNI.BOLETOS.STATS.ACTIVE') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Files -->
        <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-800/50 flex items-center justify-center">
              <span class="i-lucide-clock text-xl text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                {{ stats.pending_files }}
              </div>
              <div class="text-xs text-yellow-600/70 dark:text-yellow-400/70">
                {{ t('OMNI.BOLETOS.STATS.PENDING') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Sent This Month -->
        <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center">
              <span class="i-lucide-send text-xl text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div class="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {{ stats.sent_this_month }}
              </div>
              <div class="text-xs text-blue-600/70 dark:text-blue-400/70">
                {{ t('OMNI.BOLETOS.STATS.SENT_THIS_MONTH') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Filters -->
    <div
      class="bg-n-solid-2 border-b border-n-weak px-8 py-4"
    >
      <div class="flex items-center gap-4">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('OMNI.BOLETOS.SEARCH_PLACEHOLDER')"
          class="flex-1 min-w-0 px-2 py-1.5 text-xs border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-shadow"
        />
        <select
          v-model="filterStatus"
          class="w-36 px-4 py-3 border border-n-weak rounded-xl bg-n-alpha-3 text-n-slate-12"
        >
          <option value="all">{{ t('OMNI.BOLETOS.FILTER.ALL') }}</option>
          <option value="active">{{ t('OMNI.BOLETOS.FILTER.ACTIVE') }}</option>
          <option value="inactive">
            {{ t('OMNI.BOLETOS.FILTER.INACTIVE') }}
          </option>
        </select>
      </div>
    </div>

    <!-- Client Cards -->
    <div class="flex-1 overflow-auto px-8 py-6">
      <div v-if="loading" class="flex items-center justify-center h-64">
        <span class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500" />
      </div>

      <div
        v-else-if="filteredClients.length === 0"
        class="text-center py-16"
      >
        <span
          class="i-lucide-users text-6xl text-n-slate-8 mb-4 block mx-auto"
        />
        <p class="text-n-slate-11">{{ t('OMNI.BOLETOS.NO_CLIENTS') }}</p>
        <button
          v-if="canEditSection('boletos')"
          class="mt-4 text-woot-500 hover:text-woot-600 font-medium"
          @click="showAddModal = true"
        >
          {{ t('OMNI.BOLETOS.ADD_FIRST_CLIENT') }}
        </button>
      </div>

      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ClientCard
          v-for="client in filteredClients"
          :key="client.id"
          :ref="el => setClientCardRef(client.id, el)"
          :client="client"
          @configure="openConfig(client)"
          @upload="openUpload(client)"
          @send-now="boletoFileId => handleSendNow(client.id, boletoFileId)"
          @delete="handleDelete(client.id)"
        />
      </div>
    </div>

    <!-- Modals -->
    <AddClientModal
      :is-open="showAddModal"
      @close="showAddModal = false"
      @create="handleCreate"
    />

    <ConfigModal
      :is-open="showConfigModal"
      :client="selectedClient"
      @close="showConfigModal = false"
      @save="handleUpdate"
      @refresh="fetchClients(); fetchStats()"
    />

    <UploadModal
      :is-open="showUploadModal"
      :client="selectedClient"
      :uploading="uploading"
      @close="showUploadModal = false"
      @upload="handleUpload"
    />

    <!-- Confirm Modal -->
    <ConfirmModal
      :is-open="confirmModal.isOpen"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      :loading="confirmModal.loading"
      :confirm-text="confirmModal.confirmText"
      @confirm="executeConfirmAction"
      @cancel="closeConfirmModal"
    />
  </div>
</template>
